/**
 * V2QuoteBuilderPage + V2ClientQuotePage — TravelTrucks OS Stage 2 ("Quote & Win")
 *
 *   /admin/os/quotes/:quoteId   — internal quote builder (V2 dark cockpit)
 *   /quote/:token               — client-facing branded quote page
 *
 * Internal page is gated by the existing admin JWT. Public page is
 * token-only — no auth, no signup. Heartbeat pings the backend every 15s
 * for time-on-page tracking.
 *
 * Stripe is NOT wired. The "Secure payment" pill on the client page is a
 * placeholder slot for a future Stripe step — explicitly does not take any
 * card details. Dormant Stripe code remains untouched.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import V2Logo from './V2Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'kindra-admin-token';
const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};
const auth = () => ({ headers: { Authorization: `Bearer ${tokenStore.get()}` } });

// V2 dark palette (shared with V2AdminOSPage)
const C = {
  navy: '#100838', surface: '#1A0F44',
  hair: 'rgba(220,240,245,0.10)', hairBright: 'rgba(220,240,245,0.20)',
  icy: '#DCF0F5', muted: 'rgba(220,240,245,0.62)', faint: 'rgba(220,240,245,0.42)',
  spectrum: 'linear-gradient(95deg, #34D1B0 0%, #1FB6C1 25%, #4F8FD0 50%, #9156C4 75%, #EB258D 100%)',
};
const LEAK_COLOR = { time: '#EB258D', money: '#9156C4', lost_work: '#4F8FD0', pricing: '#1FB6C1', blind_spots: '#34D1B0' };
const LEAK_LABEL = { time: 'Time', money: 'Money', lost_work: 'Lost Work', pricing: 'Pricing', blind_spots: 'Blind Spots' };
const EFFORT_LABEL = { quick_win: 'Quick win', medium_build: 'Medium build', bigger_project: 'Bigger project' };

const fmt$ = (n, ccy = 'AUD') =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(n || 0);

const fmtDuration = (s) => {
  if (!s) return '—';
  const m = Math.floor(s / 60), sec = s % 60;
  if (m < 1) return `${sec}s`;
  if (m < 60) return `${m}m ${sec}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

// ─── Internal quote builder (V2 dark cockpit) ─────────────────────────────
const Chrome = ({ children, crumb }) => {
  const nav = useNavigate();
  return (
    <div className="min-h-screen" style={{ background: C.navy, color: C.icy }} data-testid="quote-builder-page">
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(900px 600px at -10% -10%, rgba(235,37,141,0.10), transparent 60%),'
                  + 'radial-gradient(900px 600px at 110% 10%, rgba(52,209,176,0.10), transparent 60%)' }} />
      <header className="sticky top-0 z-30" style={{ background: 'rgba(16,8,56,0.78)', backdropFilter: 'blur(14px)', borderBottom: `1px solid ${C.hair}` }}>
        <div className="max-w-[1280px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/admin/os" className="flex items-center gap-3" data-testid="quote-back-os">
            <V2Logo variant="main" tone="icy" height={26} />
            <span style={{ width: 1, height: 18, background: C.hair }} />
            <span className="text-[12px] tracking-[0.20em] uppercase font-semibold" style={{ color: C.muted }}>TravelTrucks OS · Quote</span>
          </Link>
          <button onClick={() => { tokenStore.clear(); nav('/admin/login'); }}
                  style={{ color: C.muted, fontFamily: 'Inter', fontSize: 13, background: 'transparent', border: 'none', cursor: 'pointer' }}>
            Sign out
          </button>
        </div>
        {crumb && <div className="max-w-[1280px] mx-auto px-6 pb-3 text-[12px]" style={{ color: C.faint, fontFamily: 'Inter' }}>{crumb}</div>}
      </header>
      <main className="relative z-10 max-w-[1280px] mx-auto px-6 py-8">{children}</main>
    </div>
  );
};

export const V2QuoteBuilderPage = () => {
  const { quoteId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  // ─── Send-to-client modal state ─────────────────────────────────────
  // The email NEVER fires automatically. It sends only when Col fills
  // in the recipient (HubSpot-prefilled when possible) and clicks
  // "Send to client" inside the modal — an explicit two-step gate.
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendOk, setSendOk] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/os/quotes/${encodeURIComponent(quoteId)}`, auth());
      setData(r.data);
      setDraft(JSON.parse(JSON.stringify(r.data.quote)));
    } catch (e) {
      if (e?.response?.status === 401) { tokenStore.clear(); const r=encodeURIComponent(window.location.pathname+window.location.search); window.location.assign('/admin/login?returnTo='+r); return; }
      setErr(e?.response?.data?.detail || 'Failed to load quote.');
    }
  };
  useEffect(() => { if (tokenStore.get()) load(); /* eslint-disable-next-line */ }, [quoteId]);

  if (!tokenStore.get()) return <Navigate to="/admin/login" replace />;
  if (err) return <Chrome><div style={{ color: '#FF7DB3', fontFamily: 'Inter' }}>{err}</div></Chrome>;
  if (!draft) return <Chrome><div style={{ color: C.faint, fontFamily: 'Inter' }}>Loading…</div></Chrome>;

  const total = (draft.line_items || []).reduce((s, li) => s + (Number(li.price_aud) || 0), 0);
  const isAccepted = draft.status === 'accepted';
  const isSent = ['sent', 'viewed', 'accepted'].includes(draft.status);

  const updateLineItem = (idx, field, value) => {
    const next = { ...draft, line_items: [...draft.line_items] };
    next.line_items[idx] = { ...next.line_items[idx], [field]: value };
    setDraft(next);
  };
  const addLineItem = () => {
    setDraft({ ...draft, line_items: [...(draft.line_items || []), {
      id: '', title: 'New line item', description: '', leak_area: null, effort: null, price_aud: 0,
    }] });
  };
  const removeLineItem = (idx) => {
    setDraft({ ...draft, line_items: draft.line_items.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const body = {
        title: draft.title, intro: draft.intro, validity_days: draft.validity_days,
        business_name: draft.business_name,
        line_items: draft.line_items.map((li) => ({
          id: li.id || undefined, title: li.title, description: li.description || '',
          leak_area: li.leak_area || null, effort: li.effort || null,
          price_aud: Number(li.price_aud) || 0,
        })),
      };
      const r = await axios.patch(`${API}/admin/os/quotes/${encodeURIComponent(quoteId)}`, body, auth());
      setData((d) => ({ ...d, quote: r.data.quote }));
      setDraft(JSON.parse(JSON.stringify(r.data.quote)));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const markSent = async () => {
    if (!confirm('Mark this quote as sent? This locks the validity window and writes a HubSpot note. You\u2019ll get the public link to send to the client.')) return;
    await save();
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/admin/os/quotes/${encodeURIComponent(quoteId)}/mark_sent`, {}, auth());
      setData((d) => ({ ...d, quote: r.data.quote }));
      setDraft(JSON.parse(JSON.stringify(r.data.quote)));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Mark sent failed.');
    } finally { setBusy(false); }
  };

  // ─── Send-to-client modal handlers ───────────────────────────────────
  const openSendModal = async () => {
    setErr(''); setSendOk('');
    // Pre-fill from HubSpot if we have a contact bound — best-effort.
    let prefill = data?.contact_email || draft?.email_sent_to || '';
    setSendTo(prefill);
    setSendOpen(true);
  };
  const sendToClient = async () => {
    setBusy(true); setErr(''); setSendOk('');
    try {
      // Always save the latest edits first so the recipient sees what's on screen.
      if (!isSent) await save();
      const r = await axios.post(
        `${API}/admin/os/quotes/${encodeURIComponent(quoteId)}/send_email`,
        sendTo ? { to_email: sendTo.trim() } : {},
        auth(),
      );
      setData((d) => ({ ...d, quote: r.data.quote }));
      setDraft(JSON.parse(JSON.stringify(r.data.quote)));
      setSendOk(`Email sent to ${r.data.emailed_to}. TravelTrucks has just delivered the proposal — view tracking is live.`);
      setSendOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === 'no_recipient_email') {
        setErr('No recipient email found. Enter one above (HubSpot has no email on this contact).');
      } else if (detail === 'resend_send_failed') {
        setErr('Resend rejected the send. Check the Resend dashboard / domain verification and try again.');
      } else {
        setErr(detail || 'Could not send the email.');
      }
    } finally { setBusy(false); }
  };

  return (
    <Chrome crumb={<>
      <Link to={`/admin/os/sessions/${draft.session_id}`} style={{ color: C.muted, textDecoration: 'none' }}>Review {draft.business_name}</Link>
      <span style={{ margin: '0 8px' }}>/</span> Quote builder
    </>}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <p style={{ fontFamily: 'Inter', color: C.faint, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Quote &middot; {draft.status}</p>
          <h1 style={{ fontFamily: 'Poppins', fontWeight: 600, color: C.icy, fontSize: 'clamp(26px,3.4vw,34px)', marginTop: 6 }}>Build &amp; send the proposal</h1>
        </div>
        <div className="flex items-center gap-2">
          {isSent && (
            <>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/quote/${draft.token}`} target="_blank" rel="noreferrer"
                data-testid="quote-open-public"
                style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hairBright}`, background: 'transparent', color: C.icy, fontFamily: 'Inter', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
                Open client view ↗
              </a>
              {!isAccepted && (
                <button onClick={openSendModal} disabled={busy} data-testid="quote-resend-email"
                        style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hairBright}`, background: 'transparent', color: C.icy, fontFamily: 'Inter', fontWeight: 600, fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
                  Re-send email
                </button>
              )}
            </>
          )}
          {!isSent && (
            <>
              <button onClick={save} disabled={busy} data-testid="quote-save"
                      style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hairBright}`, background: 'transparent', color: C.icy, fontFamily: 'Inter', fontWeight: 600, fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
                {busy ? 'Saving…' : 'Save draft'}
              </button>
              <button onClick={markSent} disabled={busy || total <= 0 || (draft.line_items || []).length === 0}
                      data-testid="quote-mark-sent"
                      style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hairBright}`, background: 'transparent', color: C.icy, fontFamily: 'Inter', fontWeight: 600, fontSize: 13, cursor: busy ? 'wait' : 'pointer', opacity: (total<=0||(draft.line_items||[]).length===0) ? 0.5 : 1 }}>
                Mark as sent
              </button>
              {/* Primary action — opens an explicit modal. The email
                  NEVER fires until Col clicks "Send to client" inside
                  that modal. Disabled until prices + line items exist. */}
              <button onClick={openSendModal} disabled={busy || total <= 0 || (draft.line_items || []).length === 0}
                      data-testid="quote-send-to-client"
                      style={{ padding: '10px 22px', borderRadius: 999, border: 'none', background: C.spectrum, color: '#fff', fontFamily: 'Inter', fontWeight: 700, fontSize: 13, cursor: busy ? 'wait' : 'pointer', opacity: (total<=0||(draft.line_items||[]).length===0) ? 0.5 : 1 }}>
                Send to client →
              </button>
            </>
          )}
        </div>
      </div>
      {/* Send-to-client confirmation modal — two-step gate. The actual
          fetch only fires when Col clicks the inner "Send" button.
          Closing the modal cancels the action. */}
      {sendOpen && (
        <div role="dialog" data-testid="quote-send-modal"
             style={{ position: 'fixed', inset: 0, zIndex: 60,
                       background: 'rgba(8,4,28,0.72)', backdropFilter: 'blur(6px)',
                       display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ maxWidth: 520, width: '100%', background: C.surface,
                          border: `1px solid ${C.hair}`, borderRadius: 18, padding: '28px 28px',
                          boxShadow: '0 24px 60px rgba(0,0,0,0.45)' }}>
            <p style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.faint, margin: 0 }}>Confirm</p>
            <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, color: C.icy, fontSize: 22, margin: '6px 0 14px' }}>
              Send proposal to {draft.business_name}?
            </h2>
            <p style={{ fontFamily: 'Inter', fontSize: 14, color: C.muted, lineHeight: 1.6, margin: '0 0 18px' }}>
              TravelTrucks will email the branded proposal from your business address{' '}
              with the tokenised <code style={{ background: 'rgba(220,240,245,0.06)', padding: '1px 6px', borderRadius: 4, color: C.icy }}>/quote/{draft.token?.slice(0, 10)}…</code> link.
              Nothing is sent until you click Send below.
            </p>
            <label style={{ display: 'block', fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint, marginBottom: 6 }}>Recipient email</label>
            <input value={sendTo} onChange={(e) => setSendTo(e.target.value)} type="email"
                   data-testid="quote-send-email-input"
                   placeholder="owner@theirbusiness.com"
                   style={{ width: '100%', background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`, color: C.icy, fontFamily: 'Inter', fontSize: 15, padding: '10px 14px', borderRadius: 10, outline: 'none' }} />
            <p style={{ fontFamily: 'Inter', fontSize: 12, color: C.faint, margin: '8px 0 22px' }}>
              {data?.contact_email
                ? `Pre-filled from HubSpot (${data.contact_email}).`
                : "If left blank, we'll use whatever email HubSpot has on the linked contact."}
            </p>
            {err && <p style={{ color: '#FF7DB3', fontFamily: 'Inter', fontSize: 13, marginBottom: 12 }}>{err}</p>}
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => { setSendOpen(false); setErr(''); }} disabled={busy}
                      data-testid="quote-send-cancel"
                      style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hair}`, background: 'transparent', color: C.muted, fontFamily: 'Inter', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={sendToClient} disabled={busy} data-testid="quote-send-confirm"
                      style={{ padding: '10px 22px', borderRadius: 999, border: 'none', background: C.spectrum, color: '#fff', fontFamily: 'Inter', fontWeight: 700, fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
                {busy ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
      {sendOk && (
        <div data-testid="quote-send-success" className="rounded-2xl mb-5"
             style={{ background: 'rgba(52,209,176,0.10)', border: '1px solid rgba(52,209,176,0.35)', padding: '14px 18px', color: '#5FE0BE', fontFamily: 'Inter', fontSize: 13 }}>
          ✅ {sendOk}
        </div>
      )}
      {err && <div style={{ color: '#FF7DB3', fontFamily: 'Inter', marginBottom: 12 }}>{err}</div>}
      {isSent && (
        <div className="rounded-2xl mb-5" style={{ background: 'rgba(52,209,176,0.10)', border: '1px solid rgba(52,209,176,0.30)', padding: '14px 18px' }}>
          <div style={{ fontFamily: 'Inter', fontSize: 13, color: '#5FE0BE' }}>
            <strong>Public link:</strong> <code style={{ color: C.icy, background: 'rgba(220,240,245,0.06)', padding: '2px 8px', borderRadius: 6 }}>{process.env.REACT_APP_BACKEND_URL}/quote/{draft.token}</code>
            <span style={{ marginLeft: 12, color: C.muted }}>Send this to {draft.business_name} yourself — TravelTrucks never auto-emails clients.</span>
          </div>
        </div>
      )}
      {/* Header card */}
      <section className="rounded-2xl mb-5" style={{ background: C.surface, border: `1px solid ${C.hair}`, padding: '20px 24px' }}>
        <label style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>Title</label>
        <input value={draft.title || ''} disabled={isAccepted} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          data-testid="quote-title-input"
          style={{ width: '100%', background: 'transparent', border: 'none', color: C.icy, fontFamily: 'Poppins', fontWeight: 600, fontSize: 24, padding: '6px 0', outline: 'none' }} />
        <label style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint, marginTop: 14, display: 'block' }}>Intro</label>
        <textarea value={draft.intro || ''} rows={2} disabled={isAccepted} onChange={(e) => setDraft({ ...draft, intro: e.target.value })}
          style={{ width: '100%', background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`, color: C.icy, fontFamily: 'Inter', fontSize: 14, lineHeight: 1.55, padding: '8px 12px', borderRadius: 10, outline: 'none', marginTop: 4 }} />
        <div className="flex flex-wrap gap-4 mt-3">
          <div>
            <label style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>Business</label>
            <input value={draft.business_name || ''} disabled={isAccepted} onChange={(e) => setDraft({ ...draft, business_name: e.target.value })}
              style={{ display: 'block', background: 'transparent', border: 'none', color: C.icy, fontFamily: 'Inter', fontSize: 15, outline: 'none', padding: '4px 0' }} />
          </div>
          <div>
            <label style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>Validity (days)</label>
            <input type="number" min="1" max="365" value={draft.validity_days || 30} disabled={isAccepted} onChange={(e) => setDraft({ ...draft, validity_days: Number(e.target.value) || 30 })}
              style={{ display: 'block', background: 'transparent', border: 'none', color: C.icy, fontFamily: 'Inter', fontSize: 15, outline: 'none', padding: '4px 0', width: 90 }} />
          </div>
        </div>
      </section>

      {/* Line items */}
      <section className="rounded-2xl mb-5" style={{ background: C.surface, border: `1px solid ${C.hair}` }}>
        <header className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${C.hair}` }}>
          <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, color: C.icy, fontSize: 17 }}>Line items &middot; set prices yourself</h2>
          {!isAccepted && (
            <button onClick={addLineItem} data-testid="quote-add-line"
              style={{ padding: '6px 12px', borderRadius: 999, border: `1px solid ${C.hairBright}`, background: 'transparent', color: C.icy, fontFamily: 'Inter', fontSize: 12, cursor: 'pointer' }}>
              + Add line
            </button>
          )}
        </header>
        <div className="px-6 py-4">
          {(draft.line_items || []).map((li, i) => (
            <div key={li.id || i} data-testid={`quote-line-${i}`}
                 className="rounded-xl mb-3"
                 style={{ background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`,
                            borderLeft: `3px solid ${LEAK_COLOR[li.leak_area] || C.hairBright}`,
                            padding: '14px 16px' }}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div style={{ flex: 1, minWidth: 200 }}>
                  <input value={li.title || ''} disabled={isAccepted} onChange={(e) => updateLineItem(i, 'title', e.target.value)}
                    style={{ width: '100%', background: 'transparent', border: 'none', color: C.icy, fontFamily: 'Poppins', fontWeight: 600, fontSize: 16, outline: 'none' }} />
                  <textarea value={li.description || ''} rows={2} disabled={isAccepted} onChange={(e) => updateLineItem(i, 'description', e.target.value)}
                    style={{ width: '100%', background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`, color: C.muted, fontFamily: 'Inter', fontSize: 13, lineHeight: 1.55, padding: '6px 10px', borderRadius: 8, marginTop: 6, outline: 'none', resize: 'vertical' }} />
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {li.leak_area && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontFamily: 'Inter', color: C.faint, padding: '3px 9px', borderRadius: 999, border: `1px solid ${LEAK_COLOR[li.leak_area]}55` }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: LEAK_COLOR[li.leak_area] }} /> {LEAK_LABEL[li.leak_area]}
                      </span>
                    )}
                    {li.effort && (
                      <span style={{ fontSize: 11, fontFamily: 'Inter', color: C.muted, padding: '3px 9px', borderRadius: 999, border: `1px solid ${C.hair}` }}>{EFFORT_LABEL[li.effort] || li.effort}</span>
                    )}
                  </div>
                </div>
                <div style={{ minWidth: 140, textAlign: 'right' }}>
                  <label style={{ fontFamily: 'Inter', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>Price (AUD)</label>
                  <div className="flex items-center justify-end gap-1">
                    <span style={{ color: C.muted, fontFamily: 'Inter' }}>$</span>
                    <input type="number" min="0" max="10000000" step="100" value={li.price_aud || 0} disabled={isAccepted}
                      data-testid={`quote-price-${i}`}
                      onChange={(e) => updateLineItem(i, 'price_aud', Number(e.target.value))}
                      style={{ width: 110, background: 'transparent', border: `1px solid ${C.hair}`, color: C.icy, fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, textAlign: 'right', padding: '6px 10px', borderRadius: 8, outline: 'none' }} />
                  </div>
                  {!isAccepted && (draft.line_items || []).length > 1 && (
                    <button onClick={() => removeLineItem(i)}
                      style={{ marginTop: 8, fontSize: 11, color: C.faint, background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                      remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {(draft.line_items || []).length === 0 && (
            <div className="text-center py-6" style={{ color: C.faint, fontFamily: 'Inter' }}>No line items yet — add one to get started.</div>
          )}
        </div>
        <footer className="px-6 py-4 flex items-center justify-between" style={{ borderTop: `1px solid ${C.hair}` }}>
          <span style={{ fontFamily: 'Inter', fontSize: 13, color: C.faint, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Total</span>
          <span data-testid="quote-total" style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 28, color: C.icy }}>{fmt$(total)}</span>
        </footer>
      </section>

      {/* Tracking analytics */}
      {isSent && data?.analytics && (
        <section className="rounded-2xl mb-5" style={{ background: C.surface, border: `1px solid ${C.hair}`, padding: '18px 24px' }}
                 data-testid="quote-analytics">
          <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, color: C.icy, fontSize: 17, marginBottom: 14 }}>Client engagement</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              ['Status', draft.status],
              ['Distinct viewers', data.analytics.distinct_viewers],
              ['Total opens', data.analytics.total_opens],
              ['Time on quote', fmtDuration(data.analytics.total_time_seconds)],
            ].map(([k, v]) => (
              <div key={k} style={{ background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`, borderRadius: 12, padding: '12px 14px' }}>
                <div style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>{k}</div>
                <div style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, color: C.icy, marginTop: 4 }}>{v}</div>
              </div>
            ))}
          </div>
          {data.followup_signal === 'good_time_to_follow_up' && (
            <div className="rounded-lg" style={{ background: 'rgba(235,37,141,0.10)', border: '1px solid rgba(235,37,141,0.35)', padding: '10px 14px', fontFamily: 'Inter', fontSize: 13, color: '#FF8FC1' }}>
              ✨ <strong>Good time to follow up</strong> — opened multiple times but not accepted yet.
            </div>
          )}
          <div className="mt-3">
            <div style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint, marginBottom: 6 }}>Timeline</div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {(draft.events || []).slice().reverse().slice(0, 10).map((ev, i) => (
                <li key={i} className="flex items-baseline gap-3 py-1" style={{ fontFamily: 'Inter', fontSize: 13 }}>
                  <span style={{ width: 60, color: ev.type === 'accept' ? '#5FE0BE' : ev.type === 'sent' ? '#9EC1E8' : C.muted, fontWeight: 600 }}>{ev.type}</span>
                  <span style={{ color: C.muted }}>{fmtDate(ev.ts)}</span>
                  {ev.meta?.new && <span style={{ color: C.faint, fontSize: 11 }}>(new viewer)</span>}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </Chrome>
  );
};

// ─── Client-facing branded quote page (/quote/:token) ─────────────────────
export const V2ClientQuotePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [accepting, setAccepting] = useState(false);
  const lastPingRef = useRef(Date.now());
  const visibleRef = useRef(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/quote/${encodeURIComponent(token)}`, { withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.status === 404 ? "We couldn't find that proposal." : "Couldn't load the proposal.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Heartbeat ping every 15s while page is visible
  useEffect(() => {
    if (!data) return;
    const onVisChange = () => { visibleRef.current = !document.hidden; };
    document.addEventListener('visibilitychange', onVisChange);
    const interval = setInterval(async () => {
      if (!visibleRef.current) { lastPingRef.current = Date.now(); return; }
      const now = Date.now();
      const delta = Math.min(90, Math.round((now - lastPingRef.current) / 1000));
      if (delta < 1) return;
      lastPingRef.current = now;
      try {
        await axios.post(`${API}/quote/${encodeURIComponent(token)}/ping`,
          { delta_seconds: delta }, { withCredentials: true });
      } catch (e) { /* silent — tracking is best-effort */ }
    }, 15000);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisChange); };
  }, [data, token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/quote/${encodeURIComponent(token)}/accept`, {}, { withCredentials: true });
      setData((d) => d ? { ...d, status: 'accepted', accepted_at: new Date().toISOString() } : d);
    } catch (e) {
      setErr('Could not record your acceptance. Please try again or get in touch with Col directly.');
    } finally { setAccepting(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" style={{ background: C.navy, color: C.icy }} data-testid="client-quote-error">
        <div className="text-center max-w-md">
          <V2Logo variant="main" tone="icy" height={28} />
          <p className="mt-6" style={{ fontFamily: 'Inter', color: C.muted, fontSize: 16 }}>{err}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.navy, color: C.icy }}>
        <div style={{ color: C.faint, fontFamily: 'Inter' }}>Loading…</div>
      </div>
    );
  }

  const isAccepted = data.status === 'accepted';
  const total = data.total;
  const ccy = data.currency || 'AUD';

  return (
    <div className="min-h-screen" style={{ background: C.navy, color: C.icy }} data-testid="client-quote-page">
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(900px 600px at -10% -10%, rgba(235,37,141,0.10), transparent 60%),'
                  + 'radial-gradient(900px 600px at 110% 10%, rgba(52,209,176,0.10), transparent 60%)' }} />
      <main className="relative z-10 max-w-[760px] mx-auto px-5 py-10 sm:py-16">
        {/* Brand mark */}
        <div className="flex items-center gap-3 mb-10">
          <V2Logo variant="main" tone="icy" height={28} />
          <span style={{ width: 1, height: 18, background: C.hair }} />
          <span className="text-[12px] tracking-[0.22em] uppercase font-semibold" style={{ color: C.muted }}>Proposal</span>
        </div>

        {/* Greeting */}
        <p style={{ fontFamily: 'Inter', color: C.faint, fontSize: 13, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 8 }}>
          Prepared for {data.business_name}
        </p>
        <h1 data-testid="client-quote-title"
            style={{ fontFamily: 'Poppins', fontWeight: 600, color: C.icy, fontSize: 'clamp(30px, 5.5vw, 46px)', lineHeight: 1.1, letterSpacing: '-0.01em' }}>
          {data.title}
        </h1>
        {data.intro && (
          <p style={{ fontFamily: 'Inter', color: C.muted, fontSize: 17, lineHeight: 1.65, marginTop: 14, maxWidth: 620 }}>
            {data.intro}
          </p>
        )}

        {/* Line items */}
        <section className="mt-10" data-testid="client-line-items">
          {(data.line_items || []).map((li, i) => (
            <div key={li.id || i} className="rounded-2xl mb-3"
                 data-testid={`client-line-${i}`}
                 style={{ background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`,
                          borderLeft: `3px solid ${LEAK_COLOR[li.leak_area] || C.hairBright}`,
                          padding: '18px 22px' }}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div style={{ flex: 1, minWidth: 220 }}>
                  <h3 style={{ fontFamily: 'Poppins', fontWeight: 600, color: C.icy, fontSize: 18, lineHeight: 1.3 }}>{li.title}</h3>
                  {li.description && (
                    <p style={{ fontFamily: 'Inter', color: C.muted, fontSize: 14, lineHeight: 1.65, marginTop: 8, whiteSpace: 'pre-wrap' }}>
                      {li.description}
                    </p>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 22, color: C.icy }}>{fmt$(li.price_aud, ccy)}</div>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Total */}
        <section className="rounded-2xl mt-6" style={{ background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`, padding: '20px 24px' }}>
          <div className="flex items-baseline justify-between">
            <span style={{ fontFamily: 'Inter', fontSize: 13, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.faint }}>Total proposal</span>
            <span data-testid="client-total" style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 'clamp(32px, 5vw, 40px)', color: C.icy, background: C.spectrum, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {fmt$(total, ccy)}
            </span>
          </div>
          {data.valid_until && !isAccepted && (
            <div style={{ marginTop: 8, fontFamily: 'Inter', fontSize: 12, color: C.faint }}>
              Valid until {new Date(data.valid_until).toLocaleDateString('en-AU', { dateStyle: 'medium' })}
            </div>
          )}
        </section>

        {/* Brand positioning — "you own it" — tasteful, single statement,
            NOT a cost comparison. Lives here so it lands right before accept. */}
        <section className="mt-6 rounded-2xl"
                 data-testid="client-quote-ownership-note"
                 style={{ background: 'rgba(52,209,176,0.06)', border: `1px solid ${C.hair}`,
                          borderLeft: '3px solid rgba(52,209,176,0.55)', padding: '18px 22px' }}>
          <div style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 11, letterSpacing: '0.20em',
                        textTransform: 'uppercase', color: C.muted, marginBottom: 8 }}>
            How we work
          </div>
          <p style={{ fontFamily: 'Inter', color: C.icy, fontSize: 14.5, lineHeight: 1.65, margin: 0 }}>
            Everything we build for you, you own. No ongoing software subscriptions for what we build —
            and where it makes sense, we&apos;ll connect the tools you already use rather than replace them.
          </p>
        </section>

        {/* Accept or thank-you */}
        {!isAccepted ? (
          <section className="mt-8 text-center" data-testid="client-accept-section">
            <button onClick={accept} disabled={accepting} data-testid="client-accept-btn"
                    style={{ padding: '16px 36px', borderRadius: 999, border: 'none', background: C.spectrum, color: '#fff',
                              fontFamily: 'Inter', fontWeight: 700, fontSize: 16, letterSpacing: '0.02em',
                              cursor: accepting ? 'wait' : 'pointer', boxShadow: '0 8px 24px rgba(235,37,141,0.25)' }}>
              {accepting ? 'Recording…' : 'Accept proposal'}
            </button>
            <p style={{ marginTop: 14, fontFamily: 'Inter', fontSize: 12, color: C.faint }}>
              No payment is taken at this step. Secure payment will be enabled here.
            </p>
          </section>
        ) : (
          <section className="mt-8 rounded-2xl text-center"
                   data-testid="client-accepted-state"
                   style={{ background: 'rgba(52,209,176,0.10)', border: '1px solid rgba(52,209,176,0.35)', padding: '28px 24px' }}>
            <div style={{ fontSize: 36, marginBottom: 6 }}>🎉</div>
            <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, color: C.icy, fontSize: 22 }}>Beauty — proposal accepted.</h2>
            <p style={{ fontFamily: 'Inter', color: C.muted, fontSize: 14, lineHeight: 1.65, marginTop: 8, maxWidth: 460, margin: '8px auto 0' }}>
              Col&apos;s been notified and will be in touch to lock in the start date and the rest of the detail. Cheers for trusting us with this.
            </p>
          </section>
        )}

        <footer className="mt-12 pt-6 text-center" style={{ borderTop: `1px solid ${C.hair}` }}>
          <p style={{ fontFamily: 'Inter', fontSize: 12, color: C.faint }}>
            TravelTrucks &middot; questions or changes? Reply to Col&apos;s email.
          </p>
        </footer>
      </main>
    </div>
  );
};

export default V2QuoteBuilderPage;
